import { useMemo } from "react"
import { PaginationState } from "@tanstack/react-table"
import {
  useFetchIntegrationsQuery,
  useGetIntegrationDetailsQuery,
  useGetStatusQuery,
  useFetchBrandsQuery
} from "../../redux/queries"

interface UseIntegrationsPageDataOpts {
  id?: string
  isSuperAdmin: boolean
  orgId?: string
  pagination?: PaginationState
  /** `""` = no API filter (all brands) */
  selectedBrandId?: string
}

function startOfTodayTimestamp(): number {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return Math.floor(d.getTime() / 1000)
}

export function useIntegrationsPageData({
  id,
  isSuperAdmin,
  orgId,
  pagination,
  selectedBrandId
}: UseIntegrationsPageDataOpts) {
  const orgFilter = isSuperAdmin ? {} : { org_id: orgId }
  const todayStart = useMemo(() => startOfTodayTimestamp(), [])

  const baseParams = useMemo(() => {
    const brand_id = selectedBrandId || undefined
    return {
      full_detail: true,
      ...orgFilter,
      ...(brand_id ? { brand_id } : {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, orgId, selectedBrandId])

  // Today's integrations — pinned at top, capped at 100
  const todayParams = useMemo(
    () => ({
      ...baseParams,
      min_created: todayStart,
      limit: 100,
      order_by: "created_desc"
    }),
    [baseParams, todayStart]
  )

  // Historical integrations — exclude today, newest-first, server-paginated
  const historyParams = useMemo(
    () => ({
      ...baseParams,
      max_created: todayStart,
      order_by: "created_desc",
      limit: pagination?.pageSize ?? 10,
      offset: pagination ? pagination.pageIndex * pagination.pageSize : 0
    }),
    [baseParams, todayStart, pagination]
  )

  const brandsQueryParams = useMemo(
    () => ({ limit: 999, ...orgFilter }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSuperAdmin, orgId]
  )

  const { data: brands } = useFetchBrandsQuery(brandsQueryParams)

  const { data: todayResponse, isLoading: isLoadingToday } =
    useFetchIntegrationsQuery(todayParams, { refetchOnMountOrArgChange: true })

  const { data: historyResponse, isLoading: isLoadingHistory } =
    useFetchIntegrationsQuery(historyParams, {
      refetchOnMountOrArgChange: true
    })

  const { data: integration, isLoading: isLoadingIntegration } =
    useGetIntegrationDetailsQuery(id ?? "", { skip: !id })

  const { data: statusData = [], isLoading: isLoadingStatus } =
    useGetStatusQuery(
      { integrationID: integration?.id ?? "" },
      { skip: !integration?.id }
    )

  return {
    brands,
    todayIntegrations: todayResponse?.results ?? [],
    integrations: historyResponse?.results ?? [],
    integrationsTotal: historyResponse?.total ?? 0,
    integration,
    statusData,
    isLoadingIntegrations: isLoadingToday || isLoadingHistory,
    isLoadingIntegration,
    isLoadingStatus
  }
}
